import React from 'react';
import { useVoice } from '@humeai/voice-react';

interface AudioControlsProps {
  timeRemaining: number;
  timerPaused: boolean;
  onSessionPause: () => void;
  onSessionResume: () => Promise<void>;
  onFinishInterview: () => void;
}

const AudioControls: React.FC<AudioControlsProps> = ({
  timeRemaining = 0,
  timerPaused,
  onSessionPause,
  onSessionResume,
  onFinishInterview,
}) => {
  const { status } = useVoice();

  const isConnected = status.value === 'connected';
  const isConnecting = status.value === 'connecting';

  return (
    <div className='bg-white p-4 rounded-lg shadow-lg border-t border-gray-200'>
      <div className='flex justify-between items-center'>
        <div className='flex items-center gap-3'>
          <div
            className={`w-3 h-3 rounded-full ${
              isConnected
                ? 'bg-red-500 animate-pulse'
                : isConnecting
                ? 'bg-blue-500 animate-pulse'
                : 'bg-gray-400'
            }`}
          ></div>
          <span className='text-sm font-medium text-gray-700'>
            {isConnected
              ? 'Connected'
              : isConnecting
              ? 'Reconnecting...'
              : 'Paused'}
          </span>
          {timeRemaining > 0 && (
            <div className='flex items-center gap-2 ml-4'>
              <span className='text-gray-400'>|</span>
              {timeRemaining > 0 && (
                <div
                  className={`text-sm font-mono font-medium ${
                    timeRemaining <= 300
                      ? 'text-red-600'
                      : timeRemaining <= 600
                      ? 'text-orange-600'
                      : 'text-gray-600'
                  }`}
                >
                  ⏱️{' '}
                  {Math.floor(timeRemaining / 60)
                    .toString()
                    .padStart(2, '0')}
                  :{(timeRemaining % 60).toString().padStart(2, '0')}
                </div>
              )}
            </div>
          )}
        </div>

        <div className='flex gap-2'>
          <button
            className={`px-4 py-2 text-white rounded text-sm font-medium disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors ${
              timerPaused
                ? 'bg-green-500 hover:bg-green-600'
                : 'bg-red-500 hover:bg-red-600'
            }`}
            onClick={timerPaused ? onSessionResume : onSessionPause}
            disabled={isConnecting}
          >
            {timerPaused ? 'Resume' : 'Pause'}
          </button>

          <button
            className='px-4 py-2 bg-gray-600 text-white rounded text-sm font-medium hover:bg-gray-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors'
            onClick={onFinishInterview}
            disabled={isConnecting}
          >
            Finish Interview
          </button>
        </div>
      </div>
    </div>
  );
};

export default AudioControls;
